/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaUpload, FaPencilAlt } from 'react-icons/fa';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';

import BatchImportTab from './components/BatchImportTab';
import ManualEntryTab from './components/ManualEntryTab';
import {
   getClusterOrigins,
   DEFAULT_CLUSTER_ORIGIN,
} from '../../../services/routing/routingScheduleApi';

// ---------------------------------------------------------------------------
// Permissões consumidas
// ---------------------------------------------------------------------------
const PERMISSION_BATCH_IMPORT  = 'routing:import:create';
const PERMISSION_MANUAL_CREATE = 'routing:schedule_item:create';
// Alocação (técnico/departamento/freelance) na criação manual só aparece pra
// quem tem schedule:manage — o backend valida a whitelist (422
// allocation_forbidden). Vendedor cria sem esses campos. (M4 §2)
const PERMISSION_ALLOCATE      = 'routing:schedule:manage';
// Restaurar item CANCELADO a partir do atalho de duplicata no import (handoff
// 0060 §2 → 0059): exclusiva da roteirização, sem OR com finance.
const PERMISSION_RESTORE_ITEM  = 'routing:schedule_item:cancel';
// Faixa de catálogo (handoff 0060 §1) — leitura de roteirizações.
const PERMISSION_READ_SCHEDULE = 'routing:schedule:read';

// ---------------------------------------------------------------------------
// Identificadores das abas
// ---------------------------------------------------------------------------
const TAB_BATCH  = 'BATCH';
const TAB_MANUAL = 'MANUAL';

// ---------------------------------------------------------------------------
// Default seguro para schedule_date — amanhã, no fuso local (sem toISOString
// que devolveria UTC e poderia voltar 1 dia em fusos negativos como -03:00).
// ---------------------------------------------------------------------------
function getTomorrowAsLocalIsoDate() {
   const today = new Date();
   const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
   const year  = tomorrow.getFullYear();
   const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
   const day   = String(tomorrow.getDate()).padStart(2, '0');
   return `${year}-${month}-${day}`;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------
// Switcher das abas — segmented escuro sobre o fundo do MainArea (vive no
// PageHeader, à direita do título). Estilo do DS v2 (pílulas escuras).
const HeaderTabs = styled.div`
   display: inline-flex;
   gap: 4px;
   padding: 4px;
   border-radius: 12px;
   background: rgba(255, 255, 255, 0.08);
   border: 1px solid rgba(255, 255, 255, 0.12);
   flex-shrink: 0;

   @media (max-width: 640px) {
      align-self: flex-end;
   }
`;

const HeaderTabButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 9px 16px;
   border: 0;
   border-radius: 9px;
   background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
   color: ${({ $active, theme }) =>
      ($active
         ? theme?.colors?.textPrimary ?? '#2b3038'
         : 'rgba(255, 255, 255, 0.7)')};
   box-shadow: ${({ $active }) =>
      ($active ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none')};
   font-size: 13.5px;
   font-weight: 600;
   white-space: nowrap;
   cursor: pointer;
   transition:
      background 0.14s ease,
      color 0.14s ease;

   &:hover:not(:disabled) {
      color: ${({ $active }) => ($active ? undefined : '#fff')};
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
   }
`;

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------
export default function RoutingItemNew() {
   const userPermissions = useSelector(
      (state) => state.auth.user?.permissions ?? [],
   );

   const canBatchImport = useMemo(
      () => userPermissions.includes(PERMISSION_BATCH_IMPORT),
      [userPermissions],
   );
   const canManualCreate = useMemo(
      () => userPermissions.includes(PERMISSION_MANUAL_CREATE),
      [userPermissions],
   );
   const canAllocate = useMemo(
      () => userPermissions.includes(PERMISSION_ALLOCATE),
      [userPermissions],
   );
   const canRestoreItem = useMemo(
      () => userPermissions.includes(PERMISSION_RESTORE_ITEM),
      [userPermissions],
   );
   const canViewCatalog = useMemo(
      () => userPermissions.includes(PERMISSION_READ_SCHEDULE),
      [userPermissions],
   );

   // ------------------------------------------------------------------------
   // State compartilhado entre abas — preservado ao trocar de tab
   // ------------------------------------------------------------------------
   const [scheduleDate, setScheduleDate]     = useState(getTomorrowAsLocalIsoDate);
   const [schedulePeriod, setSchedulePeriod] = useState('MANHA');

   // Base operacional (Ponto 4) — default GOIANIA (operação principal). Vale
   // tanto para o lote quanto para a inserção manual; preservado ao trocar de aba.
   const [clusterOrigin, setClusterOrigin]   = useState(DEFAULT_CLUSTER_ORIGIN);
   const [clusterOrigins, setClusterOrigins] = useState([]);

   useEffect(() => {
      let cancelled = false;
      getClusterOrigins()
         .then((list) => {
            if (!cancelled) setClusterOrigins(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            // Silencioso: sem a lista, o seletor cai pro default GOIANIA e o
            // backend aplica o mesmo default se o campo não for enviado.
         });
      return () => {
         cancelled = true;
      };
   }, []);

   // Aba inicial: honra ?tab= quando permitido (usado pelo "Reagendar" do board,
   // que abre esta tela em outra guia já na aba Manual). Sem param válido,
   // prioriza Lote se o usuário tiver acesso, caso contrário Manual.
   const [searchParams] = useSearchParams();
   const requestedTab = searchParams.get('tab');
   let initialTab = canBatchImport ? TAB_BATCH : TAB_MANUAL;
   if (requestedTab === TAB_MANUAL && canManualCreate) initialTab = TAB_MANUAL;
   else if (requestedTab === TAB_BATCH && canBatchImport) initialTab = TAB_BATCH;
   const [activeTab, setActiveTab] = useState(initialTab);

   // ------------------------------------------------------------------------
   // Guard: nenhuma permissão → 403
   // ------------------------------------------------------------------------
   if (!canBatchImport && !canManualCreate) {
      return <Navigate to="/403" replace />;
   }

   // ------------------------------------------------------------------------
   // Render
   // ------------------------------------------------------------------------
   const sharedTabProps = {
      scheduleDate,
      schedulePeriod,
      onScheduleDateChange:   setScheduleDate,
      onSchedulePeriodChange: setSchedulePeriod,
      clusterOrigin,
      clusterOrigins,
      onClusterOriginChange:  setClusterOrigin,
   };

   // Só faz sentido mostrar o switcher quando o usuário pode acessar AS DUAS
   // abas; com uma só, a aba disponível é renderizada direto.
   const showTabs = canBatchImport && canManualCreate;

   return (
      <Container>
         <PageHeader>
            <PageHeaderText>
               <PageTitle>Adicionar rotas</PageTitle>
               <PageSubtitle>
                  Importe a planilha do Refiltek ou cadastre items avulsos
               </PageSubtitle>
            </PageHeaderText>

            {showTabs && (
               <HeaderTabs role="tablist" aria-label="Modos de adição de rotas">
                  <HeaderTabButton
                     type="button"
                     role="tab"
                     aria-selected={activeTab === TAB_BATCH}
                     $active={activeTab === TAB_BATCH}
                     onClick={() => setActiveTab(TAB_BATCH)}
                  >
                     <FaUpload size={14} /> Importar planilha
                  </HeaderTabButton>
                  <HeaderTabButton
                     type="button"
                     role="tab"
                     aria-selected={activeTab === TAB_MANUAL}
                     $active={activeTab === TAB_MANUAL}
                     onClick={() => setActiveTab(TAB_MANUAL)}
                  >
                     <FaPencilAlt size={13} /> Inserção manual
                  </HeaderTabButton>
               </HeaderTabs>
            )}
         </PageHeader>

         {/* Ambas as abas trazem os próprios cards (FormCard) — renderizam direto
             sobre o fundo, sem Section wrapper, para evitar "card sobre card".
             O rework 0060 do Lote passou a ser cards/modal (antes era tabela
             dentro do Section). */}
         {activeTab === TAB_BATCH && canBatchImport && (
            <BatchImportTab
               {...sharedTabProps}
               canRestore={canRestoreItem}
               canViewCatalog={canViewCatalog}
            />
         )}

         {activeTab === TAB_MANUAL && canManualCreate && (
            <ManualEntryTab {...sharedTabProps} canAllocate={canAllocate} />
         )}
      </Container>
   );
}
